import './index.less';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import React, { Suspense, useEffect, useRef, useMemo } from 'react';
import { useState } from 'react';
import { ToLoginOrRegister } from '@/components/popup/ToLoginOrRegister';
import { SpinLoading } from 'antd-mobile';
import { gotoActivity } from '@/utils';
import { usePromocaoList } from '@/store';
import BottomBar from '@/components/BottomBar';
import classNames from 'classnames';
import { ACTIVITY_DATA } from '@/consts';

const AgentCenter = React.lazy(() => import('@/views/Activities/AgentCenter'));
const VIP = React.lazy(() => import('@/views/Activities/VIP'));
const Retention = React.lazy(() => import('@/views/Activities/Retention'));
const Cashback = React.lazy(() => import('@/views/Activities/Cashback'));
const Cashwheel = React.lazy(() => import('@/views/Activities/Cashwheel'));
const Nivel = React.lazy(() => import('@/views/Activities/Nivel'));
const GiftCode = React.lazy(() => import('@/views/Activities/GiftCode'));

type PromocaoItem = {
  id: string;
  showRedPoint: boolean;
  link: string;
  activityType: string;
  bannerUrl: string;
};

type PromocaoList = [string, PromocaoItem[]][];

interface iTopTab {
  type: string;
  tabName?: string;
  activityType?: string;
  link?: string;
  comp?: React.LazyExoticComponent<React.FC>;
  id?: string;
  linkName?: string;
  showRedPoint?: boolean;
}

const compList: iTopTab[] = [
  {
    type: 'AGENT_REWARD',
    comp: AgentCenter
  },
  {
    type: 'VIP_REWARD',
    comp: VIP
  },
  {
    type: 'LEVEL_REWARD',
    comp: Nivel
  },
  {
    type: 'AGENT_RETURN',
    comp: Cashback
  },
  {
    type: 'REMAIN',
    comp: Retention
  },
  {
    type: 'REDEEM',
    comp: GiftCode
  },
  {
    type: 'PINDUODUO_REWARD',
    comp: Cashwheel
  }
].map(item => ({
    ...item,
    linkName: ACTIVITY_DATA[item.type].linkName
}));

const Promocao = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(false);
  const [pTabIndex, setPTabIndex] = useState(+localStorage.pTabIndex || 0);

  const pList = usePromocaoList(s => s.list);
  const singleList = usePromocaoList(s => s.singleList);

  const sortPromocaoList = () => {
    const orderList = [
      'Tudo',
      'Depósito',
      'Auxílio',
      'Roda',
      'Baú',
      'Rebate',
      'Chuva de Dinheiro',
      'Evento'
    ]
    const list: [string, PromocaoItem[]][] = []

    orderList.forEach(name => {
      const item = pList.find(([listName]) => listName === name)
      if (item) list.push(item)
    });

    return list
  }

  const promocaoList: PromocaoList = useMemo(() => sortPromocaoList(), [pList]);

  const topTabs = useMemo(() => {
    return [{
      type: 'promocao',
      name: 'Promoção',
    }, ...singleList].map(tab => {
      const target = compList.find(comp => comp.type == tab.activityType)
      if (target) return {
        ...tab,
        comp: target.comp,
        linkName: target.linkName,
      }
      return tab
    })
  }, [singleList])

  const getSubComp = (linkName: string) => {
    return topTabs.find(item => item.linkName == linkName)?.comp || null
  }

  const [tabIndex, setTabIndex] = useState(0);
  const { type } = useParams();
  const Component = type ? getSubComp(type) : null;
  const tabRef = useRef<HTMLDivElement>(null);
  const setCurrentTab = (i: number) => {
    setTabIndex(i);
    tabRef.current?.getElementsByTagName('span')[i].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }

  useEffect(() => {
    if (type) {
      topTabs.forEach((item, i) => {
        if (item.linkName == type) setCurrentTab(i)
      });
    } else setCurrentTab(0)
  }, [type, Component])

  return (
    <>
      <div className="promocaoList">
        <div className="topTabs pr-4" ref={tabRef}>
          {topTabs.map(({ name, link, id, showRedPoint }, i) => {
            return (
              <span
                key={i}
                className={classNames('tab', {
                  on: tabIndex === i
                })}
                onClick={() => {
                  const newIndex = i;
                  setTabIndex(newIndex);

                  const newParams = new URLSearchParams(searchParams);
                  if (newIndex === 0) {
                    newParams.delete('activityId');
                    navigate(`/promocao?${newParams}`);
                  } else if (link) {
                    newParams.set('activityId', id!);
                    navigate(`${link}?${newParams}`);
                  }
                }}
              >
                {name}
                {showRedPoint && <span className="redPoint"></span>}
              </span>
            );
          })}
        </div>

        <div className="con max-h-[calc(100vh-135px)]">
          {tabIndex == 0 && !Component  ? (
            <div className="pList">
              <div className="leftBar">
                {
                  promocaoList.map(([name, list], index) => (
                    <div
                      className={classNames('tab', {
                        on: pTabIndex == index
                      })}
                      onClick={() => {
                        setPTabIndex(index);
                        localStorage.pTabIndex = index;
                      }}
                      title={name}
                      key={index}
                    >
                      {name}
                      {list.some(item => item.showRedPoint) && (
                        <span className="redPoint"></span>
                      )}
                    </div>
                  ))
                }
              </div>

              <div className="pContent">
                {promocaoList[pTabIndex]?.[1] ? (
                  promocaoList[pTabIndex][1].map(
                    ({ id, showRedPoint, link, activityType, bannerUrl }) => {
                      return (
                        <div
                          className="mb-4 ml-4"
                          key={id}
                          onClick={() => gotoActivity(link, id)}
                        >
                          <div className="flex items-center justify-center">
                            <div className="flex-auto relative">
                              <img
                                src={bannerUrl}
                                alt={activityType}
                                className="h-[103px] rounded-lg"
                              />
                              {showRedPoint && (
                                <i className="w-3 h-3 rounded-full bg-[#EE0A24] absolute top-2 right-2" />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )
                ) : (
                  <div className="w-full flex justify-center h-full items-center">
                    <SpinLoading color="currentColor" />
                  </div>
                )}
                {isOpen && (
                  <ToLoginOrRegister isOpen={isOpen} setIsOpen={setIsOpen} />
                )}
              </div>
            </div>
          ) : (
            <div className="pb-20">
              <Suspense
                fallback={
                  <div className="w-full flex justify-center h-full items-center">
                    <SpinLoading color="currentColor" />
                  </div>
                }
              >
                {Component && <Component />}
              </Suspense>
            </div>
          )}
        </div>
      </div>
      <BottomBar />
    </>
  );
};

export default Promocao;
